import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SignUp.css'; 
import logo from '../Logo/logo-Photoroom.png'; 
import api from '../services/api';

const API_SIGNUP_URL = api.defaults.baseURL + '/auth/signup';

// --- Icon Components for a professional UI ---
const IconUser = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const IconEmail = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>;
const IconLock = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>;
const IconBriefcase = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;
const IconSpinner = () => <svg className="spinner" viewBox="0 0 50 50"><circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle></svg>;
const IconPhone = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92V19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2.08a16.93 16.93 0 0 1-1.92-8.92A16.93 16.93 0 0 1 4.08 4H6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2h1.92A16.93 16.93 0 0 1 22.92 8a16.93 16.93 0 0 1-1.92 8.92z"></path><path d="M16.24,7.76a6,6,0,0,1,8.48,8.48l-3,3a6,6,0,0,1-8.48-8.48Z"></path></svg>;

const SignUp = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'broker' });
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Core logic remains untouched as requested
    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prevData => ({ ...prevData, [id]: value }));
        if (errors[id] || errors.api) {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[id];
                delete newErrors.api;
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        let newErrors = {};
        let isValid = true;
        if (!formData.name) { newErrors.name = 'Name is required'; isValid = false; }
        if (!formData.email) { newErrors.email = 'Email is required'; isValid = false; }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) { newErrors.email = 'Email address is invalid'; isValid = false; }
        if (!formData.password) { newErrors.password = 'Password is required'; isValid = false; }
        else if (formData.password.length < 6) { newErrors.password = 'Password must be at least 6 characters'; isValid = false; }
        if (formData.password !== formData.confirmPassword) { newErrors.confirmPassword = 'Passwords do not match'; isValid = false; }
        if (!formData.role) { newErrors.role = 'Role is required'; isValid = false; }
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMessage('');
        setErrors({});
        if (!validateForm()) { return; }
        
        setIsLoading(true);
        const apiData = { name: formData.name, email: formData.email, password: formData.password, phoneNumber: formData.phone, role: formData.role };

        try {
            const response = await fetch(API_SIGNUP_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(apiData),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'The server responded with an error.');
            }
            setSuccessMessage("Account created successfully! Redirecting to login...");
            setTimeout(() => { navigate('/'); }, 2000);
        } catch (error) {
            setErrors({ api: `Signup failed: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    };

      const [passwordShown, setPasswordShown] = useState(false);
      const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);


    return (
        <div className="signup-container">
            {/* Left side for branding (consistent with login) */}
            <div className="signup-branding">
                <div className="branding-content" >
                    <img src={logo} alt="Logo" style={{height:"100px", width:"80%"}}/>
                    <h3 className="branding-subtitle">Estates at White Oak - Luxury Living in Western Wake County, NC</h3>
                </div>
            </div>

            {/* Right side for the sign-up form */}
            <div className="signup-form-container">
                <div className="signup-form-card">
                    <h2 className="signup-title">Get Started</h2>
                    <p className="signup-subtitle">It's free to sign up and only takes a minute.</p>

                    <form onSubmit={handleSubmit} noValidate>
                        {successMessage && <div className="alert alert-success">{successMessage}</div>}
                        {errors.api && <div className="alert alert-error">{errors.api}</div>}

                        <div className="input-group">
                            <label htmlFor="name">Full Name</label>
                            <div className={`input-with-icon ${errors.name ? 'is-invalid' : ''}`}>
                                <IconUser />
                                <input type="text" id="name" value={formData.name} onChange={handleChange} placeholder="e.g., John Doe" />
                            </div>
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <div className="input-group">
                            <label htmlFor="email">Email Address</label>
                            <div className={`input-with-icon ${errors.email ? 'is-invalid' : ''}`}>
                                <IconEmail />
                                <input type="email" id="email" value={formData.email} onChange={handleChange} placeholder="your.email@example.com" />
                            </div>
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>
                        <div className="input-group">
                            <label htmlFor="phone">Phone Number</label>
                            <div className={`input-with-icon ${errors.phone ? 'is-invalid' : ''}`}>
                                <IconPhone />
                                <input type="text" id="phone" value={formData.phone} onChange={handleChange} placeholder="e.g., (123) 456-7890" />
                            </div>
                            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                        </div>
                        
                        <div className="input-group">
                                <label htmlFor="password">Password</label>
                                <div className={`input-with-icon ${errors.password ? 'is-invalid' : ''}`} style={{ position: 'relative' }}>
                                    <IconLock />
                                    <input
                                    type={passwordShown ? "text" : "password"}
                                    id="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Minimum 6 characters"
                                    />
                                    <span
                                    style={{
                                        position: "absolute",
                                        right: "1rem",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#00A8A8",
                                        fontWeight: 600,
                                        fontSize: "0.95rem"
                                    }}
                                    onClick={() => setPasswordShown((prev) => !prev)}
                                    >
                                    {passwordShown ? "Hide" : "Show"}
                                    </span>
                                </div>
                                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                                </div>

                   
                        <div className="input-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className={`input-with-icon ${errors.confirmPassword ? 'is-invalid' : ''}`} style={{ position: 'relative' }}>
                                <IconLock />
                                <input
                                    type={confirmPasswordShown ? "text" : "password"}
                                    id="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Re-enter your password"
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        right: "1rem",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#00A8A8",
                                        fontWeight: 600,
                                        fontSize: "0.95rem"
                                    }}
                                    onClick={() => setConfirmPasswordShown((prev) => !prev)}
                                >
                                    {confirmPasswordShown ? "Hide" : "Show"}
                                </span>
                            </div>
                            {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                        </div>


                        <div className="input-group">
                            <label htmlFor="role">Sign up as a...</label>
                            <div className={`input-with-icon ${errors.role ? 'is-invalid' : ''}`}>
                                <IconBriefcase />
                                <select id="role" value={formData.role} onChange={handleChange} >
                                
                                    <option value="broker">Broker</option>
                                
                                </select>
                            </div>
                            {errors.role && <div className="invalid-feedback">{errors.role}</div>}
                        </div>

                        <button type="submit" className="btn-signup" disabled={isLoading}>
                            {isLoading ? <IconSpinner /> : 'Create Account'}
                        </button>

                        <p className="signup-footer">
                            Already have an account? <Link to="/">Sign In</Link>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SignUp;